import { Box, Center, Progress, Text } from '@chakra-ui/react';
import {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { GridPagination } from './GridPagination';

export interface GridTableProps<TData extends object> {
  /** Column definitions for AG Grid */
  columnDefs: ColDef<TData>[];
  /** Row data to display */
  rowData: TData[];
  /** Enable pagination (default: true) */
  pagination?: boolean;
  /** Default page size (default: 50) */
  defaultPageSize?: number;
  /** Page size options for pagination dropdown */
  pageSizeOptions?: number[];
  /** Show loading indicator */
  isLoading?: boolean;
  /** Height of the grid container */
  height?: string | number;
  /** Minimum height of the grid container */
  minHeight?: string | number;
  /** Callback when a row is clicked */
  onRowClick?: (data: TData) => void;
  /** Column IDs to skip for row click (e.g. ['actions'] for action buttons/dropdowns) */
  suppressRowClickForColumns?: string[];
  /** Enable quick filter (global search) */
  quickFilterText?: string;
  /** Auto-size columns to fit content on first data render */
  autoSizeColumnsToFitContent?: boolean;
  /** Auto-size columns when pagination changes */
  autoSizeOnPaginationChange?: boolean;
  /** Disable column virtualisation for autosize (required for accurate measurement) */
  disableColumnVirtualisationForAutosize?: boolean;
  /** Additional AG Grid props */
  gridOptions?: Omit<
    AgGridReactProps<TData>,
    'columnDefs' | 'rowData' | 'pagination'
  >;
}

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_HEIGHT = 500;
const DEFAULT_MIN_HEIGHT = 550;

/**
 * Reusable AG Grid table component with custom Diamond theme
 * Features:
 * - Blue header matching project design system
 * - Indonesian locale pagination ("Halaman X dari Y")
 * - Sorting, filtering, and sticky columns support
 * - Loading state and empty state handling
 */
export function GridTable<TData extends object>({
  columnDefs,
  rowData,
  pagination = true,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions,
  isLoading = false,
  height = DEFAULT_HEIGHT,
  minHeight = DEFAULT_MIN_HEIGHT,
  onRowClick,
  suppressRowClickForColumns,
  quickFilterText,
  autoSizeColumnsToFitContent = false,
  autoSizeOnPaginationChange = false,
  disableColumnVirtualisationForAutosize = false,
  gridOptions,
}: GridTableProps<TData>) {
  const gridRef = useRef<AgGridReact<TData>>(null);
  const [gridApi, setGridApi] = useState<GridApi<TData> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const defaultColDef = useMemo<ColDef<TData>>(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
      sortable: true,
    }),
    []
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent<TData>) => {
      setGridApi(params.api);
      const api = params.api;
      const totalRowCount = api.getDisplayedRowCount();
      const currentPageSize = api.paginationGetPageSize();
      const currentPageNumber = api.paginationGetCurrentPage() + 1;
      const totalPageCount = api.paginationGetTotalPages();

      setTotalRows(totalRowCount);
      setPageSize(currentPageSize);
      setCurrentPage(currentPageNumber);
      setTotalPages(totalPageCount);
      gridOptions?.onGridReady?.(params);
    },
    [gridOptions]
  );

  const handlePaginationChanged = useCallback(() => {
    if (gridApi) {
      const totalRowCount = gridApi.getDisplayedRowCount();
      const currentPageSize = gridApi.paginationGetPageSize();
      const currentPageNumber = gridApi.paginationGetCurrentPage() + 1;
      const totalPageCount = gridApi.paginationGetTotalPages();

      setTotalRows(totalRowCount);
      setPageSize(currentPageSize);
      setCurrentPage(currentPageNumber);
      setTotalPages(totalPageCount);

      if (autoSizeOnPaginationChange) {
        gridApi.autoSizeAllColumns();
      }
    }
  }, [gridApi, autoSizeOnPaginationChange]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (gridApi) {
        gridApi.paginationGoToPage(page - 1);
      }
    },
    [gridApi]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      if (gridApi) {
        // AG Grid v32: update page size by setting options
        gridApi.updateGridOptions({ paginationPageSize: size });
      }
    },
    [gridApi]
  );

  const handleFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent<TData>) => {
      if (autoSizeColumnsToFitContent && params.api) {
        params.api.autoSizeAllColumns();
      }
      gridOptions?.onFirstDataRendered?.(params);
    },
    [autoSizeColumnsToFitContent, gridOptions]
  );

  const handleRowClicked = useCallback(
    (event: { data: TData | undefined }) => {
      if (event.data && onRowClick) {
        onRowClick(event.data);
      }
    },
    [onRowClick]
  );

  const handleCellClicked = useCallback(
    (event: {
      data: TData | undefined;
      column?: { getColId: () => string };
    }) => {
      if (!event.data || !onRowClick) return;
      const colId = event.column?.getColId?.();
      if (colId && suppressRowClickForColumns?.includes(colId)) return;
      onRowClick(event.data);
    },
    [onRowClick, suppressRowClickForColumns]
  );

  const rowClickHandlers = useMemo(() => {
    if (!onRowClick) return {};
    if (suppressRowClickForColumns?.length) {
      return { onCellClicked: handleCellClicked };
    }
    return { onRowClicked: handleRowClicked };
  }, [
    onRowClick,
    suppressRowClickForColumns,
    handleCellClicked,
    handleRowClicked,
  ]);

  const noRowsOverlayComponent = useMemo(
    () => () =>
      (
        <Center h="100%" py={8}>
          <Text fontSize="lg" color="gray.500">
            Data tidak ditemukan
          </Text>
        </Center>
      ),
    []
  );

  return (
    <Box>
      {isLoading && (
        <Progress size="xs" isIndeterminate colorScheme="messenger" />
      )}
      <Box
        className="ag-theme-alpine ag-theme-diamond"
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          minHeight:
            typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
        }}
      >
        <AgGridReact<TData>
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          pagination={pagination}
          paginationPageSize={pageSize}
          suppressPaginationPanel={true}
          suppressColumnVirtualisation={disableColumnVirtualisationForAutosize}
          onGridReady={handleGridReady}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
          {...rowClickHandlers}
          quickFilterText={quickFilterText}
          noRowsOverlayComponent={noRowsOverlayComponent}
          animateRows={true}
          {...gridOptions}
        />
      </Box>
      {pagination && (
        <GridPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalRows={totalRows}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Box>
  );
}

export default GridTable;
